import { IncidentTemplate } from '@seen/core/models/incidentTemplate'
import { ExtendedLibrary } from '@seen/core/models/library'
import { NoteTemplate } from '@seen/core/models/noteTemplate'
import { OrderTemplate } from '@seen/core/models/orderTemplate'
import { Button } from '@seen/ui'
import { IconDots, IconPlus } from '@tabler/icons-react'
import type { ColumnDef } from '@tanstack/react-table'
import { compact, orderBy, uniqBy } from 'lodash-es'
import { type JSX, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { PractitionerDisplay } from '#src/components/display/PractitionerDisplay.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterComposer } from '#src/components/filter/FilterComposer.tsx'
import { useFilteredData } from '#src/components/filter/useFilteredData.ts'
import { NoteTemplateLabelsContextMenuSub } from '#src/components/NoteTemplateLabelsContextMenuSub.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { isAllowedTemplateType } from '#src/pages/templates/templateWhitelist.ts'
import { extractTextFromHTML, extractTitleFromHTML } from '#src/utils/html.ts'

export function TemplatesPage(): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const navigate = useNavigate()
  const navigateFromClick = useNavigateFromClickEvent()

  const { data: templates } = useSearch(ExtendedLibrary, {
    _sort: '-_lastUpdated',
    _count: '1000',
    'status:not': 'retired',
  })

  // The shared ExtendedLibrary query returns every template type; restrict the
  // table to the allow-listed ones here so other consumers stay unaffected.
  const allowedTemplates = useMemo(
    () => templates?.filter(isAllowedTemplateType),
    [templates]
  )

  const [filter, setFilter] = useState<Filter[]>([])

  const typeOptions = useMemo(
    () =>
      uniqBy(
        compact(
          allowedTemplates?.map((t) => {
            if (!t.topicCode) {
              return null
            }

            return {
              label: t.topic ?? '',
              value: t.topicCode ?? '',
              keywords: [t.topicCode ?? '', t.topic ?? ''],
            }
          }) ?? []
        ),
        'value'
      ),
    [allowedTemplates]
  )

  const filterOptions: FilterConfigMap<ExtendedLibrary> = useMemo(
    () => ({
      title: {
        type: 'text',
        label: 'Title',
        valueFn: (template) => template.title ?? '',
      },
      type: {
        type: 'multi-select',
        label: 'Type',
        options: typeOptions,
        valueFn: (template) => template.topicCode ?? '',
      },
    }),
    [typeOptions]
  )

  const filteredTemplates = useFilteredData(
    allowedTemplates,
    filterOptions,
    filter
  )

  // Note-template ids, computed once per data load so the per-row context-menu
  // callback can decide which rows get the Labels menu without re-running
  // `isInstance` (and serializing each resource) on every render.
  const noteTemplateIds = useMemo(
    () =>
      new Set(
        allowedTemplates
          ?.filter((t) => NoteTemplate.isInstance(t.toFhirResource()))
          .map((t) => t.id)
      ),
    [allowedTemplates]
  )

  const createMutation = useCreateMutation<ExtendedLibrary>()
  const mutateTemplate = usePatchMutation<ExtendedLibrary>().mutate

  const columns: ColumnDef<ExtendedLibrary>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) =>
          row.original.title
            ? extractTextFromHTML(row.original.title)
            : 'Untitled',
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => row.original.topic,
      },
      {
        accessorKey: 'lastUpdatedBy',
        header: 'Updated by',
        cell: ({ row }) => {
          const author = row.original.lastUpdatedByPractitioner
          return author ? <PractitionerDisplay practitioner={author} /> : null
        },
      },
      {
        accessorKey: 'lastUpdated',
        header: 'Last updated',
        cell: ({ row }) =>
          row.original.lastUpdated ? (
            <HoverDate date={row.original.lastUpdated} />
          ) : null,
      },
      {
        id: 'actions',
        cell: ({ row }) => {
          // This column only offers delete; read-only users get no write action.
          if (isReadOnly) {
            return null
          }
          const template = row.original
          return (
            <div className="flex items-center justify-end gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="-my-3">
                    <IconDots className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenuDeleteItem
                    deleteConfirmationProps={{
                      title: `Delete "${extractTitleFromHTML(template.title)}"?`,
                      description:
                        'Deleted templates can still be recovered by contacting our team.',
                      onConfirm: () => {
                        mutateTemplate({
                          instance: template,
                          mutateFn: (t) => {
                            t.status = 'retired'
                          },
                        })
                      },
                    }}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [mutateTemplate, isReadOnly]
  )

  if (!filteredTemplates) {
    return <Loader />
  }

  const tableData = orderBy(filteredTemplates, ['lastUpdated'], ['desc'])

  return (
    <PageLayout
      title="Templates"
      actions={
        !isReadOnly ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="flex items-center gap-2"
                disabled={createMutation.isPending}
              >
                <IconPlus className="size-4" />
                New template…
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                onClick={() => {
                  createMutation.mutate(new NoteTemplate(), {
                    onSuccess: (createdTemplate) => {
                      void navigate(`/templates/${createdTemplate.id}`)
                    },
                  })
                }}
              >
                Note template
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  createMutation.mutate(OrderTemplate.build(), {
                    onSuccess: (createdTemplate) => {
                      void navigate(`/templates/${createdTemplate.id}`)
                    },
                  })
                }}
              >
                Order template
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  createMutation.mutate(new IncidentTemplate(), {
                    onSuccess: (createdTemplate) => {
                      void navigate(`/templates/${createdTemplate.id}`)
                    },
                  })
                }}
              >
                Incident template
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : undefined
      }
    >
      <FilterComposer
        id="templates-page-filters"
        filterOptions={filterOptions}
        onChange={setFilter}
      />
      <DataTable
        columns={columns}
        data={tableData}
        onRowClick={(row, e) => navigateFromClick(`/templates/${row.id}`, e)}
        contextMenuContent={
          isReadOnly
            ? undefined
            : (row) =>
                noteTemplateIds.has(row.id) ? (
                  <NoteTemplateLabelsContextMenuSub template={row} />
                ) : null
        }
        deleteRowProps={
          isReadOnly
            ? undefined
            : (row) => ({
                title: `Delete "${extractTextFromHTML(row.title)}"?`,
                description:
                  'Deleted templates can still be recovered by contacting our team.',
                onConfirm: () => {
                  mutateTemplate({
                    instance: row,
                    mutateFn: (t) => {
                      t.status = 'retired'
                    },
                  })
                },
              })
        }
      />
    </PageLayout>
  )
}
